/**
 * select.tsx
 * ─────────────────────────────────────────────────────────────
 * Cognisun CRM — Standard Select / Dropdown Component
 *
 * DESIGN CONTRACT — SelectTrigger (matches Input exactly):
 *   Height:    40px  (h-10)
 *   Radius:    10px  (rounded-[10px])
 *   Border:    1px solid #E2E8F0
 *   Background: #FFFFFF
 *   Text:      13px, #0F172A
 *   Shadow:    0 1px 2px rgba(0,0,0,0.04) — uniform, same as Input
 *
 *   hover  → border #CBD5E1  (shadow unchanged — no expansion)
 *   focus  → border #008ED3  + base shadow + thin primary glow ring
 *   disabled → bg #F8FAFC, text #9CA3AF, opacity-60
 *
 * DESIGN CONTRACT — SelectContent (dropdown panel):
 *   Radius:  12px (rounded-xl)  ← card-level radius
 *   Shadow:  DS elevated shadow (0 8px 24px rgba(0,0,0,0.12))
 *   Border:  1px solid #E2E8F0
 *   BG:      #FFFFFF
 *
 * DESIGN CONTRACT — SelectItem:
 *   Height:  36px min  (py-2 px-3)
 *   Radius:  6px (rounded-md)
 *   Hover:   bg-[#F8FAFC]
 *   Active / selected: bg-[#EBF6FD] text-[#008ED3]
 */

import * as React from "react";
import * as SelectPrimitive from "@radix-ui/react-select";
import {
  Check,
  ChevronDown,
  ChevronUp,
  Circle,
} from "lucide-react";

import { cn } from "./utils";

const Select = SelectPrimitive.Root;
const SelectGroup = SelectPrimitive.Group;

const SelectValue = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Value>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Value>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.Value
    ref={ref}
    data-slot="select-value"
    className={cn("truncate whitespace-nowrap overflow-hidden min-w-0", className)}
    {...props}
  />
));
SelectValue.displayName = SelectPrimitive.Value.displayName;

// ─── SelectTrigger — mirrors Input exactly ────────────────────
const SelectTrigger = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Trigger> & {
    size?: "sm" | "default";
    hideChevron?: boolean;
    hideRadio?: boolean;
  }
>(({ className, size = "default", hideChevron = false, hideRadio, children, ...props }, ref) => (
  <SelectPrimitive.Trigger
    ref={ref}
    data-slot="select-trigger"
    data-size={size}
    className={cn(
      // ── Layout — same as Input ──
      "group flex h-10 w-full items-center justify-between gap-2",
      "min-w-0 overflow-hidden",
      // ── Shape ──
      "rounded-[10px]",
      // ── Border ──
      "border border-[#E2E8F0]",
      // ── Background & text ──
      "bg-white",
      "px-3 py-2",
      "text-[13px] font-normal text-[#0F172A]",
      // ── Placeholder ──
      "data-[placeholder]:text-[#9CA3AF]",
      // ── Cursor ──
      "cursor-pointer",
      // ── Subtle elevation — identical to Input ──
      "shadow-[0_1px_2px_rgba(0,0,0,0.04)]",
      // ── Transitions — shadow excluded so it never grows on hover ──
      "transition-[border-color,background-color] duration-150",
      "outline-none",
      // ── Hover: minimal border shift — shadow unchanged ──
      "hover:border-[#CBD5E1]",
      // ── Focus: primary blue border + base shadow + thin glow ring ──
      "focus:border-[#008ED3]",
      "focus:shadow-[0_1px_2px_rgba(0,0,0,0.04),0_0_0_2px_rgba(0,142,211,0.14)]",
      "focus:ring-0",
      // ── Disabled ──
      "disabled:bg-[#F8FAFC] disabled:text-[#9CA3AF]",
      "disabled:border-[#E2E8F0] disabled:cursor-not-allowed disabled:opacity-60",
      // ── Value slot text overflow ──
      "*:data-[slot=select-value]:truncate",
      "*:data-[slot=select-value]:whitespace-nowrap",
      "*:data-[slot=select-value]:overflow-hidden",
      "*:data-[slot=select-value]:min-w-0",
      "*:data-[slot=select-value]:flex",
      "*:data-[slot=select-value]:items-center",
      "*:data-[slot=select-value]:gap-2",
      // ── SVG inside trigger ──
      "[&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
      "[&_svg:not([class*='text-'])]:text-[#9CA3AF]",
      className,
    )}
    {...props}
  >
    <span className="flex items-center min-w-0 overflow-hidden flex-1">
      {children}
    </span>
    {!hideChevron && (
      <ChevronDown
        className="size-4 text-[#9CA3AF] shrink-0 ml-1 transition-transform duration-200 group-data-[state=open]:rotate-180"
        aria-hidden="true"
      />
    )}
  </SelectPrimitive.Trigger>
));
SelectTrigger.displayName = SelectPrimitive.Trigger.displayName;

// ─── SelectContent — dropdown panel ──────────────────────────
const SelectContent = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Content>
>(({ className, children, position = "popper", ...props }, ref) => (
  <SelectPrimitive.Portal>
    <SelectPrimitive.Content
      ref={ref}
      data-slot="select-content"
      className={cn(
        // ── Shell — card-level radius + elevated DS shadow ──
        "bg-white rounded-xl border border-[#E2E8F0]",
        "shadow-[0_8px_24px_rgba(0,0,0,0.12)]",
        // ── Scroll / size ──
        "relative z-50 overflow-x-hidden overflow-y-auto",
        "max-h-(--radix-select-content-available-height)",
        "min-w-[8rem]",
        // ── Animation ──
        "origin-(--radix-select-content-transform-origin)",
        "data-[state=open]:animate-in data-[state=closed]:animate-out",
        "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
        "data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95",
        "data-[side=bottom]:slide-in-from-top-2",
        "data-[side=left]:slide-in-from-right-2",
        "data-[side=right]:slide-in-from-left-2",
        "data-[side=top]:slide-in-from-bottom-2",
        position === "popper" &&
          "data-[side=bottom]:translate-y-1 data-[side=left]:-translate-x-1 data-[side=right]:translate-x-1 data-[side=top]:-translate-y-1",
        className,
      )}
      position={position}
      {...props}
    >
      <SelectScrollUpButton />
      <SelectPrimitive.Viewport
        className={cn(
          "p-1.5",
          position === "popper" &&
            "h-[var(--radix-select-trigger-height)] w-full w-[var(--radix-select-trigger-width)] scroll-my-1",
        )}
      >
        {children}
      </SelectPrimitive.Viewport>
      <SelectScrollDownButton />
    </SelectPrimitive.Content>
  </SelectPrimitive.Portal>
));
SelectContent.displayName = SelectPrimitive.Content.displayName;

// ─── SelectLabel — group heading ──────────────────────────────
const SelectLabel = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Label>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Label>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.Label
    ref={ref}
    data-slot="select-label"
    className={cn(
      "text-[11px] font-semibold text-[#9CA3AF] uppercase tracking-wide px-2 py-1.5",
      className,
    )}
    {...props}
  />
));
SelectLabel.displayName = SelectPrimitive.Label.displayName;

// ─── SelectItem — individual option ──────────────────────────
const SelectItem = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Item> & {
    hideRadio?: boolean;
  }
>(({ className, children, hideRadio = false, ...props }, ref) => {
  const textContent = typeof children === "string" ? children : undefined;
  return (
    <SelectPrimitive.Item
      ref={ref}
      data-slot="select-item"
      className={cn(
        // ── Layout ──
        "relative flex w-full cursor-pointer items-center gap-2",
        "rounded-md",
        "py-2 pr-3",
        hideRadio ? "pl-3" : "pl-3",
        // ── Typography ──
        "text-[13px] font-normal text-[#0F172A]",
        // ── States ──
        "outline-none select-none transition-colors duration-100",
        "focus:bg-[#F8FAFC] focus:text-[#0F172A]",
        "data-[highlighted]:bg-[#F8FAFC] data-[highlighted]:text-[#0F172A]",
        "data-[state=checked]:bg-[#EBF6FD] data-[state=checked]:text-[#008ED3]",
        // ── Disabled ──
        "data-[disabled]:pointer-events-none data-[disabled]:opacity-40",
        // ── SVG ──
        "overflow-hidden [&_svg:not([class*='text-'])]:text-[#9CA3AF]",
        className,
      )}
      title={textContent}
      {...props}
    >
      {!hideRadio && (
        <span className="flex size-4 items-center justify-center shrink-0">
          <SelectPrimitive.ItemIndicator>
            <Circle className="size-4 fill-[#008ED3] text-[#008ED3]" aria-hidden="true" />
          </SelectPrimitive.ItemIndicator>
          <Circle className="size-4 text-gray-300 data-[state=checked]:hidden" aria-hidden="true" />
        </span>
      )}
      <SelectPrimitive.ItemText>
        <span className="truncate whitespace-nowrap overflow-hidden block">{children}</span>
      </SelectPrimitive.ItemText>
    </SelectPrimitive.Item>
  );
});
SelectItem.displayName = SelectPrimitive.Item.displayName;

// ─── SelectSeparator ──────────────────────────────────────────
const SelectSeparator = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Separator>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Separator>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.Separator
    ref={ref}
    data-slot="select-separator"
    className={cn("bg-[#E2E8F0] pointer-events-none -mx-1 my-1.5 h-px", className)}
    {...props}
  />
));
SelectSeparator.displayName = SelectPrimitive.Separator.displayName;

// ─── Scroll Buttons ───────────────────────────────────────────
const SelectScrollUpButton = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.ScrollUpButton>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollUpButton>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.ScrollUpButton
    ref={ref}
    data-slot="select-scroll-up-button"
    className={cn("flex cursor-default items-center justify-center py-1 text-[#9CA3AF]", className)}
    {...props}
  >
    <ChevronUp className="size-4" aria-hidden="true" />
  </SelectPrimitive.ScrollUpButton>
));
SelectScrollUpButton.displayName = SelectPrimitive.ScrollUpButton.displayName;

const SelectScrollDownButton = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.ScrollDownButton>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollDownButton>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.ScrollDownButton
    ref={ref}
    data-slot="select-scroll-down-button"
    className={cn("flex cursor-default items-center justify-center py-1 text-[#9CA3AF]", className)}
    {...props}
  >
    <ChevronDown className="size-4" aria-hidden="true" />
  </SelectPrimitive.ScrollDownButton>
));
SelectScrollDownButton.displayName = SelectPrimitive.ScrollDownButton.displayName;

export {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectScrollDownButton,
  SelectScrollUpButton,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
};